<template>
  <div class="content">
    <h2 class="text-2xl font-bold mb-4">Embedding the Wheel</h2>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Can I embed Spin the Names on my website?</h3>
      <p>Yes! You can easily embed a Spin the Names wheel on your own website, blog, or online platform. This is perfect
        for:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>Teachers who want to include interactive elements in online lessons</li>
        <li>Content creators looking to add engagement to their websites</li>
        <li>Event organizers creating interactive online experiences</li>
        <li>Businesses running promotions or contests</li>
        <li>Anyone who wants to share a specific wheel configuration</li>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">How to Embed a Wheel</h3>
      <p>There are two ways to embed a wheel on your website:</p>

      <div class="mt-4 mb-4">
        <h4 class="text-lg font-medium mb-2">Option 1: Embed a Shared Wheel</h4>
        <ol class="list-decimal ml-6">
          <li>Create and customize your wheel as desired</li>
          <li>Save your wheel to your account (requires login)</li>
          <li>Click the "Share" button to generate a shareable link</li>
          <li>In the Share dialog, click the "Embed" tab</li>
          <li>Copy the provided iframe code</li>
          <li>Paste the code into your website's HTML where you want the wheel to appear</li>
        </ol>

        <div class="bg-stone-100 dark:bg-stone-800 p-4 rounded mt-3 mb-3 overflow-x-auto">
          <code class="text-sm">
&lt;iframe src="https://spinthenames.com/shared/ABC123" width="600" height="600" frameborder="0" allowfullscreen&gt;&lt;/iframe&gt;
          </code>
        </div>
      </div>

      <div class="mt-4 mb-4">
        <h4 class="text-lg font-medium mb-2">Option 2: Embed with Custom Configuration</h4>
        <p>For more advanced users, you can embed a wheel with a custom configuration directly:</p>
        <ol class="list-decimal ml-6">
          <li>Add the iframe code to your HTML</li>
          <li>Include your wheel configuration as URL parameters</li>
        </ol>

        <div class="bg-stone-100 dark:bg-stone-800 p-4 rounded mt-3 mb-3 overflow-x-auto">
          <code class="text-sm">
&lt;iframe src="https://spinthenames.com/embed?names=Option1,Option2,Option3&colors=ff0000,00ff00,0000ff" width="600" height="600" frameborder="0" allowfullscreen&gt;&lt;/iframe&gt;
          </code>
        </div>
      </div>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Customizing the Embedded Wheel</h3>
      <p>You can customize the appearance and behavior of your embedded wheel:</p>

      <h4 class="text-lg font-medium mt-4 mb-2">Size Attributes</h4>
      <p>Adjust the width and height attributes in the iframe tag to fit your website:</p>
      <div class="bg-stone-100 dark:bg-stone-800 p-4 rounded mt-3 mb-3 overflow-x-auto">
        <code class="text-sm">
width="600" height="600"  <!-- Change these values to resize the wheel -->
        </code>
      </div>

      <h4 class="text-lg font-medium mt-4 mb-2">URL Parameters (for Option 2)</h4>
      <table class="min-w-full border border-stone-200 dark:border-stone-700 mt-3 mb-3">
        <thead class="bg-stone-100 dark:bg-stone-800">
          <tr>
            <th class="py-2 px-4 border-b border-stone-200 dark:border-stone-700 text-left">Parameter</th>
            <th class="py-2 px-4 border-b border-stone-200 dark:border-stone-700 text-left">Description</th>
            <th class="py-2 px-4 border-b border-stone-200 dark:border-stone-700 text-left">Example</th>
          </tr>
        </thead>
        <tbody>
          <tr class="border-b border-stone-200 dark:border-stone-700">
            <td class="py-2 px-4">names</td>
            <td class="py-2 px-4">Comma-separated list of names/options</td>
            <td class="py-2 px-4"><code>names=Red,Blue,Green</code></td>
          </tr>
          <tr class="border-b border-stone-200 dark:border-stone-700">
            <td class="py-2 px-4">colors</td>
            <td class="py-2 px-4">Comma-separated list of hex colors (without #)</td>
            <td class="py-2 px-4"><code>colors=ff0000,00ff00,0000ff</code></td>
          </tr>
          <tr class="border-b border-stone-200 dark:border-stone-700">
            <td class="py-2 px-4">hideTitles</td>
            <td class="py-2 px-4">Set to 1 to hide wheel title</td>
            <td class="py-2 px-4"><code>hideTitles=1</code></td>
          </tr>
          <tr>
            <td class="py-2 px-4">autoSpin</td>
            <td class="py-2 px-4">Set to 1 to automatically spin on load</td>
            <td class="py-2 px-4"><code>autoSpin=1</code></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Embedded Wheel Limitations</h3>
      <p>Please note the following limitations with embedded wheels:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>Some features may be restricted in embedded mode for security reasons</li>
        <li>The embedded wheel will inherit your saved settings if using a shared wheel link</li>
        <li>Users cannot save changes made to the embedded wheel</li>
        <li>Custom sound options may be limited in some browsers</li>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Terms of Use for Embedded Wheels</h3>
      <p>When embedding Spin the Names on your website:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>You may not modify the wheel's functionality or appearance beyond the provided options</li>
        <li>The Spin the Names logo and attribution must remain visible</li>
        <li>You may not use the embedded wheel for illegal activities or in violation of our Terms of Service</li>
        <li>For commercial usage with high traffic volumes, please contact us at ash@spinthenames.com</li>
      </ul>
    </div>

    <p class="mt-8 text-sm"><a href="#top" class="text-blue-500 hover:underline">Back to top</a></p>
  </div>
</template>

<script setup>
import '@components/ui/faq/faqStyles.css';
</script>
